'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Box, Tooltip } from '@mui/material';
import { Info, Sparkles, TrendingUp } from 'lucide-react';

import { EmptyState, ErrorState } from '@/components/';
import { ReferralSkeleton } from '@/components/skeletons/pages/referrals';
import { useAuth, useSignInHandlers, useT } from '@/hooks';
import { useReferralData } from '@/services/query/referral.queries';
import { useAppDispatch } from '@/store';
import { openModal } from '@/store/slices/ui.slice';
import {
  ReferralHeader,
  ReferralHistory,
  ReferralLinkCard,
  ReferralStats,
  SlotGrid,
} from './index';

/**
 * ReferralDashboard Component
 * The main container for the referrals page.
 * Handles the tier-based logic (Standard vs Ambassador) and data orchestration.
 */
export const ReferralDashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { onLogin } = useSignInHandlers(() => {});
  const { t } = useT();
  const { data, isLoading: isDataLoading, error } = useReferralData(isAuthenticated);
  const router = useRouter();
  const dispatch = useAppDispatch();

  const isLoading = isAuthLoading || (isAuthenticated && isDataLoading);

  useEffect(() => {
    if (!isAuthLoading && isAuthenticated && user && !user.isReferredUser) {
      router.push('/');
      dispatch(openModal('referral'));
    }
  }, [isAuthLoading, isAuthenticated, user, router, dispatch]);

  if (isLoading) {
    return <ReferralSkeleton />;
  }

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-7xl space-y-8 pb-20">
        <ReferralHeader />
        <div className="rounded-3xl border border-edge bg-surface-secondary p-12">
          <EmptyState
            title={t('referral.joinTitle')}
            description={t('referral.joinDesc')}
            actionLabel={t('referral.loginToStart')}
            onAction={onLogin}
            icon={<Sparkles className="text-blue-400" size={32} />}
          />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <ErrorState title={t('referral.unavailableTitle')} message={t('referral.unavailableMsg')} />
    );
  }

  const { tier, myReferralCode, stats, slots } = data;

  const isAmbassador = tier === 'Ambassador';
  const slotsAtCapacity = slots.used >= slots.unlocked;

  return (
    <div className="mx-auto max-w-6xl space-y-8 pb-20">
      {/* Header Section */}
      <ReferralHeader />

      {/* Main Referral Card */}
      <ReferralLinkCard code={myReferralCode} isAmbassador={isAmbassador} />

      {/* Statistics Section */}
      <ReferralStats
        totalReferred={stats.totalReferrals}
        totalVolume={stats.lifetimeTradingVolume}
        estimatedRewards={stats.totalEarningsUsd}
      />

      {/* Tier-Specific Section */}
      {!isAmbassador ? (
        <section className="rounded-3xl border border-edge bg-surface-secondary p-8">
          <div className="mb-8 flex flex-col justify-between gap-6 md:flex-row md:items-center">
            <div>
              <h2 className="mb-1 flex items-center gap-2 text-xl font-bold text-content-primary">
                {t('referral.slots')}
                <div
                  className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-tighter ${
                    slotsAtCapacity
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-surface-hover text-content-muted'
                  }`}
                >
                  {slots.used} / {slots.unlocked} {t('referral.used')}
                  {slotsAtCapacity ? ` · ${t('referral.full')}` : ''}
                </div>
              </h2>
              <p className="text-sm text-content-secondary">{t('referral.slotDesc')}</p>
            </div>

            {/* Progress Bar for next slot */}
            <div className="min-w-[280px] rounded-2xl border border-edge bg-surface-primary p-4">
              <div className="mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-content-muted">
                  <TrendingUp size={12} /> {t('referral.nextSlotProgress')}
                  <Tooltip
                    title={t('referral.moreToTrade', {
                      remaining: slots.nextSlotProgress.remainingVolume,
                      slot: slots.unlocked + 1,
                    })}
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        cursor: 'help',
                        color: 'text.disabled',
                        '&:hover': { color: 'text.secondary' },
                      }}
                    >
                      <Info size={14} />
                    </Box>
                  </Tooltip>
                </span>
                <span className="text-xs font-bold text-blue-400">
                  {Number(slots.nextSlotProgress.percent).toFixed(2)}%
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-surface-hover">
                <Box
                  style={{ width: `${slots.nextSlotProgress.percent}%` }}
                  className="h-full bg-gradient-to-r from-blue-600 to-indigo-500"
                />
              </div>
              <p className="mt-2 text-[10px] text-content-muted">
                {t('referral.moreToTrade', {
                  remaining: slots.nextSlotProgress.remainingVolume,
                  slot: slots.unlocked + 1,
                })}
              </p>
            </div>
          </div>

          <SlotGrid
            used={slots?.used}
            available={slots?.unlocked}
            max={typeof slots.total === 'number' ? slots.total : 20}
          />

          <div className="mt-8 flex items-start gap-3 rounded-xl border border-blue-500/10 bg-blue-500/5 p-4">
            <Info size={18} className="mt-0.5 shrink-0 text-blue-400" />
            <p className="text-xs leading-relaxed text-content-secondary">
              <span className="font-bold text-blue-400">{t('referral.proTip')}</span>{' '}
              {t('referral.proTipDesc')}
            </p>
          </div>
        </section>
      ) : (
        <section className="rounded-3xl border border-blue-500/20 bg-gradient-to-br from-blue-600/10 to-indigo-600/10 p-8">
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500 text-white shadow-lg shadow-blue-500/20">
              <Sparkles size={24} />
            </div>
            <div>
              <h2 className="mb-0.5 text-xl font-bold text-content-primary">
                {t('referral.ambassadorTitle')}
              </h2>
              <p className="text-sm font-medium text-blue-400/80">{t('referral.ambassadorDesc')}</p>
            </div>
          </div>
          <p className="mb-4 max-w-2xl text-sm leading-relaxed text-content-secondary">
            {t('referral.ambassadorInfo')}
          </p>
        </section>
      )}

      {/* Referral Table Section */}
      <ReferralHistory />
    </div>
  );
};
